import { useEffect, useState } from 'react';

/**
 * Tracks which top-level modules a pre-cohort `user` has explored so we can
 * pull them toward Apply after they've tasted enough of the app.
 *
 * Persistence is intentionally localStorage-only: this is a nudge signal, not
 * a source of truth. If the user clears storage, the banner just restarts —
 * no server round-trip, no auth coupling.
 */
const KEY = 'founderos.user.modules.v1';

// The 12 top-level surfaces we count as "modules". Order defines the
// "X of Y" copy shown in the banner.
const MODULE_MATCHERS = [
  { key: 'venture', match: (p) => p.startsWith('/app/venture') && !p.startsWith('/app/ventures') },
  { key: 'copilot', match: (p) => p.startsWith('/app/copilot') },
  { key: 'learn', match: (p) => p.startsWith('/app/learn') },
  { key: 'calendar', match: (p) => p.startsWith('/app/calendar') },
  { key: 'journal', match: (p) => p.startsWith('/app/journal') },
  { key: 'canopy', match: (p) => p.startsWith('/app/canopy') },
  { key: 'mentors', match: (p) => p.startsWith('/app/mentors') },
  { key: 'community', match: (p) => p.startsWith('/app/community') },
  { key: 'messages', match: (p) => p.startsWith('/app/messages') },
  { key: 'network', match: (p) => p.startsWith('/app/network') || p.startsWith('/app/u/') },
  { key: 'match', match: (p) => p.startsWith('/app/match') },
  { key: 'events', match: (p) => p.startsWith('/app/events') },
  { key: 'discover', match: (p) => p.startsWith('/app/discover') },
  { key: 'ventures-explore', match: (p) => p.startsWith('/app/ventures/explore') },
  { key: 'investors', match: (p) => p.startsWith('/app/investors') },
  { key: 'internships', match: (p) => p.startsWith('/app/internships') },
];

export const TOTAL_MODULES = MODULE_MATCHERS.length;

const readSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) || '[]')); }
  catch { return new Set(); }
};
const writeSet = (set) => {
  try { localStorage.setItem(KEY, JSON.stringify([...set])); } catch { /* noop */ }
};

/**
 * Record a visit to the given pathname (matches to a module bucket).
 * No-op if it doesn't match a tracked module.
 */
export const recordModuleVisit = (pathname) => {
  if (!pathname) return;
  const hit = MODULE_MATCHERS.find((m) => m.match(pathname));
  if (!hit) return;
  const cur = readSet();
  if (cur.has(hit.key)) return;
  cur.add(hit.key);
  writeSet(cur);
  // Broadcast so any mounted `useModulesExplored` can refresh without a poll.
  window.dispatchEvent(new CustomEvent('founderos:modules-explored'));
};

/**
 * Hook — returns { count, total } and re-renders when a new module is
 * recorded (via the custom event above).
 */
export const useModulesExplored = () => {
  const [count, setCount] = useState(() => readSet().size);
  useEffect(() => {
    const on = () => setCount(readSet().size);
    window.addEventListener('founderos:modules-explored', on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener('founderos:modules-explored', on);
      window.removeEventListener('storage', on);
    };
  }, []);
  return { count, total: TOTAL_MODULES };
};
